import React, { useCallback, useMemo, useRef } from 'react'
import { get, isEmpty, isNil } from 'lodash'
import { IconPlus } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Drag, Drop, DragAndDrop } from '@/components/dragDrop'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'
import Select from '@/components/forms/Select'
import { ErrorMessage } from '@/components/forms/ErrorMessage'

import { ToolFieldRow } from './ToolFieldRow'
import { ToolConfigJsonEditor } from './ToolConfigJsonEditor'
import { TOOL_SPEAKERS, toolSpeakerDefaultOptions } from './tools.constants'
import {
  applyFieldPatch,
  applyToolSpeaker,
  getConfigErrorMessages,
  getValueTypeOptions,
  moveItem,
  newToolField,
  parseConfigJson,
  stringifyConfig,
  toFormConfig,
} from './tools.helpers'

export const AutoCaptureFields = ({
  values,
  errors,
  touched,
  submitCount,
  setFieldValue,
  setFieldTouched,
  fieldTypes,
  limits,
  savedConfig,
}) => {
  const { fields } = values.config
  const valueTypeOptions = useMemo(() => getValueTypeOptions(fieldTypes), [fieldTypes])
  const fieldsError = get(errors, 'config.fields')
  const listError = typeof fieldsError === 'string' && submitCount > 0 ? fieldsError : null
  const rowErrors = Array.isArray(fieldsError) ? fieldsError : []
  const fieldsTouched = get(touched, 'config.fields')
  const rowTouched = Array.isArray(fieldsTouched) ? fieldsTouched : []
  const configErrors = submitCount > 0 ? getConfigErrorMessages(errors) : []
  const jsonState = useMemo(
    () => (values.jsonMode ? parseConfigJson(values.configJson) : null),
    [values.jsonMode, values.configJson]
  )
  const jsonError = jsonState?.error || get(errors, 'configJson')

  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  const updateFields = useCallback(
    (nextFields) => setFieldValue('config.fields', nextFields),
    [setFieldValue]
  )
  const handleFieldChange = useCallback(
    (index, patch) => updateFields(applyFieldPatch(fieldsRef.current, index, patch)),
    [updateFields]
  )
  const handleFieldBlur = useCallback(
    (index, name) => setFieldTouched(`config.fields[${index}].${name}`, true),
    [setFieldTouched]
  )
  const handleFieldRemove = useCallback(
    (index) => updateFields(fieldsRef.current.filter((_, other) => other !== index)),
    [updateFields]
  )

  // The row a click on "Add field" just created, so its label takes the caret as it mounts.
  const pendingFocusRef = useRef(null)
  const handleLabelFocused = useCallback(() => {
    pendingFocusRef.current = null
  }, [])
  const addField = () => {
    const field = newToolField()
    pendingFocusRef.current = field.uiId
    updateFields([...fields, field])
  }

  const handleDragEnd = ({ source, destination }) => {
    if (isNil(destination) || destination.index === source.index) {
      return
    }
    updateFields(moveItem(fields, source.index, destination.index))
  }

  const jsonInvalid = values.jsonMode && isNil(jsonState?.config)

  const setJsonMode = (enabled) => {
    if (enabled === values.jsonMode) {
      return
    }
    if (enabled) {
      setFieldValue('configJson', stringifyConfig(values.config))
      setFieldValue('jsonMode', true)
      return
    }
    if (jsonState?.config) {
      setFieldValue('config', toFormConfig(jsonState.config, fieldTypes, savedConfig))
      setFieldValue('jsonMode', false)
    }
  }

  return (
    <Card
      title="Fields"
      buttons={
        <div className="tool-fields-toolbar">
          <span className="tool-fields-count" data-testid="tool-fields-count">
            {fields.length} of {limits.fields}
          </span>
          <SegmentedControl
            small
            items={[
              {
                accessor: 'fields',
                label: 'Fields',
                active: !values.jsonMode,
                disabled: jsonInvalid,
                tooltip: jsonInvalid ? 'Fix the JSON before switching back' : undefined,
                onClick: () => setJsonMode(false),
                dataTestId: 'tool-editor-fields',
              },
              {
                accessor: 'json',
                label: 'JSON',
                active: values.jsonMode,
                onClick: () => setJsonMode(true),
                dataTestId: 'tool-json-toggle',
              },
            ]}
          />
        </div>
      }
    >
      {!values.jsonMode && (
        <FormFieldGrid className="tool-fields-speaker">
          <FormField label="Speaker" span={6}>
            <div className="form-caption">
              Whose speech the fields are captured from. Per field lets each field choose.
            </div>
            <Select
              options={toolSpeakerDefaultOptions}
              value={values.config.speaker || TOOL_SPEAKERS.PER_FIELD}
              isClearable={false}
              inputId="tool-speaker"
              aria-label="Speaker"
              onChange={(option) =>
                setFieldValue(
                  'config',
                  applyToolSpeaker(values.config, option?.value ?? TOOL_SPEAKERS.PER_FIELD)
                )
              }
              data-testid="tool-speaker"
            />
          </FormField>
        </FormFieldGrid>
      )}
      {values.jsonMode ? (
        <ToolConfigJsonEditor
          value={values.configJson}
          error={jsonError}
          onChange={(text) => setFieldValue('configJson', text)}
        />
      ) : (
        <>
          {listError && <ErrorMessage content={listError} />}
          {configErrors.map((message) => (
            <ErrorMessage key={message} content={message} />
          ))}
          {isEmpty(fields) && (
            <p className="tool-form__filters-description">
              Add the details Balto should capture from the call.
            </p>
          )}
          <DragAndDrop onDragEnd={handleDragEnd}>
            <div className="tool-fields-list">
              <Drop droppableId="tool-fields" type="tool-fields">
                {fields.map((field, index) => (
                  <Drag key={field.uiId} draggableId={field.uiId} index={index} alwaysShowIcon>
                    <ToolFieldRow
                      field={field}
                      index={index}
                      fieldTypes={fieldTypes}
                      valueTypeOptions={valueTypeOptions}
                      toolSpeaker={values.config.speaker}
                      limits={limits}
                      focusLabelOnMount={pendingFocusRef.current === field.uiId}
                      onLabelFocused={handleLabelFocused}
                      rowErrors={rowErrors[index]}
                      rowTouched={rowTouched[index]}
                      submitCount={submitCount}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      onRemove={handleFieldRemove}
                    />
                  </Drag>
                ))}
              </Drop>
            </div>
          </DragAndDrop>
          <div className="tool-fields-footer">
            <Button
              onClick={addField}
              disabled={fields.length >= limits.fields}
              disabledTooltip={`A form has at most ${limits.fields} fields`}
              data-testid="tool-add-field"
            >
              <IconPlus />
              Add field
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
